import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Input,
  Modal,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE } from "../../config/api";

const { Title, Text } = Typography;

const shellStyle = {
  maxWidth: 1180,
  margin: "0 auto",
  padding: 18,
};

const glassCardStyle = {
  borderRadius: 24,
  border: "1px solid #eef2f6",
  boxShadow: "0 12px 32px rgba(15, 23, 42, 0.05)",
  overflow: "hidden",
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: 15,
  fontWeight: 700,
  color: "#0f172a",
};

const fieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 12,
};

const statCardStyle = {
  borderRadius: 18,
  border: "1px solid #f1f5f9",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
};

const panelSectionStyle = {
  ...glassCardStyle,
  borderRadius: 20,
};

const labelStyle = {
  display: "block",
  color: "#475467",
  fontSize: 12,
  fontWeight: 700,
  marginBottom: 6,
};

const createEmptyBookingGroup = (dateValue = null) => ({
  id: `group-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  categoryId: null,
  subCategoryId: null,
  serviceId: null,
  employeeId: null,
  date: dateValue,
  time: null,
  availableSlots: [],
  loadingSlots: false,
});

const CreateReservation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedDate = searchParams.get("date");
  const preselectedUserId = searchParams.get("userId");

  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [detectedUser, setDetectedUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: "",
    lastName: "",
    phone: "",
    email: "",
    type_client: "reliable",
  });
  const [bookingGroups, setBookingGroups] = useState([
    createEmptyBookingGroup(preselectedDate || null),
  ]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem("token");
        const [usersResponse, categoriesResponse] = await Promise.all([
          axios.get(`${API_BASE}/users`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE}/category`),
        ]);

        setUsers(Array.isArray(usersResponse.data?.users) ? usersResponse.data.users : []);
        setCategories(Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []);
      } catch (error) {
        message.error("No se pudieron cargar usuarios o servicios");
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!users.length) {
      return;
    }

    syncDetectedUserByEmail(userForm.email);
  }, [users, userForm.email]);

  useEffect(() => {
    if (!preselectedUserId || !users.length) {
      return;
    }

    const matchedUser =
      users.find((user) => String(user.id) === String(preselectedUserId)) || null;

    if (!matchedUser) {
      return;
    }

    setSelectedUserId(matchedUser.id);
    setDetectedUser(matchedUser);
    setUserForm({
      name: matchedUser.name || "",
      lastName: matchedUser.lastName || "",
      phone: matchedUser.phone || "",
      email: matchedUser.email || "",
      type_client: matchedUser.type_client || "reliable",
    });
  }, [preselectedUserId, users]);

  const findServiceById = useMemo(() => {
    const serviceMap = new Map();

    categories.forEach((category) => {
      (category.SubCategories || []).forEach((subCategory) => {
        (subCategory.Services || []).forEach((service) => {
          serviceMap.set(Number(service.id), {
            ...service,
            categoryId: category.id,
            subCategoryId: subCategory.id,
            categoryName: category.name,
            subCategoryName: subCategory.name,
          });
        });
      });
    });

    return (serviceId) => serviceMap.get(Number(serviceId)) || null;
  }, [categories]);

  const summary = useMemo(() => {
    const selectedServices = bookingGroups
      .map((group) => findServiceById(group.serviceId))
      .filter(Boolean);

    return {
      totalServices: bookingGroups.length,
      scheduledServices: bookingGroups.filter((group) => group.date && group.time).length,
      totalReservation: selectedServices.reduce(
        (sum, service) => sum + Number(service?.price || 0),
        0
      ),
      totalDuration: selectedServices.reduce(
        (sum, service) => sum + Number(service?.duration || 0),
        0
      ),
    };
  }, [bookingGroups, findServiceById]);

  const syncDetectedUserByEmail = (emailValue) => {
    const normalizedEmail = String(emailValue || "").trim().toLowerCase();

    if (!normalizedEmail) {
      setSelectedUserId(null);
      setDetectedUser(null);
      return;
    }

    const matchedUser =
      users.find(
        (user) => String(user.email || "").trim().toLowerCase() === normalizedEmail
      ) || null;

    if (!matchedUser) {
      setSelectedUserId(null);
      setDetectedUser(null);
      return;
    }

    setSelectedUserId(matchedUser.id);
    setDetectedUser(matchedUser);
    setUserForm((current) => ({
      ...current,
      name: matchedUser.name || current.name,
      lastName: matchedUser.lastName || current.lastName,
      phone: matchedUser.phone || current.phone,
      email: matchedUser.email || current.email,
      type_client: matchedUser.type_client || current.type_client,
    }));
  };

  const updateGroup = (groupId, changes) => {
    setBookingGroups((currentGroups) =>
      currentGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              ...changes,
            }
          : group
      )
    );
  };

  const fetchAvailableSlots = async (groupId, nextDate, nextServiceId, nextEmployeeId) => {
    if (!nextDate || !nextServiceId || !nextEmployeeId) {
      updateGroup(groupId, { availableSlots: [], time: null, loadingSlots: false });
      return;
    }

    updateGroup(groupId, { loadingSlots: true });

    try {
      const response = await axios.get(`${API_BASE}/timeSlot/available`, {
        params: {
          date: nextDate,
          serviceId: nextServiceId,
          employeeId: nextEmployeeId,
        },
      });

      updateGroup(groupId, {
        availableSlots: Array.isArray(response.data) ? response.data : [],
        time: null,
        loadingSlots: false,
      });
    } catch (error) {
      updateGroup(groupId, { availableSlots: [], time: null, loadingSlots: false });
      message.error("No se pudieron cargar los horarios");
    }
  };

  const handleServiceChange = (groupId, serviceId) => {
    const currentGroup = bookingGroups.find((group) => group.id === groupId);
    updateGroup(groupId, {
      serviceId,
      employeeId: null,
      time: null,
      availableSlots: [],
    });

    if (currentGroup?.date) {
      fetchAvailableSlots(groupId, currentGroup.date, serviceId, null);
    }
  };

  const handleCategoryChange = (groupId, categoryId) => {
    updateGroup(groupId, {
      categoryId,
      subCategoryId: null,
      serviceId: null,
      employeeId: null,
      time: null,
      availableSlots: [],
    });
  };

  const handleSubCategoryChange = (groupId, subCategoryId) => {
    updateGroup(groupId, {
      subCategoryId,
      serviceId: null,
      employeeId: null,
      time: null,
      availableSlots: [],
    });
  };

  const handleEmployeeChange = (groupId, employeeId) => {
    const currentGroup = bookingGroups.find((group) => group.id === groupId);
    updateGroup(groupId, {
      employeeId,
      time: null,
      availableSlots: [],
    });

    fetchAvailableSlots(groupId, currentGroup?.date || null, currentGroup?.serviceId, employeeId);
  };

  const handleDateChange = (groupId, dateValue) => {
    const normalizedDate = dateValue ? dayjs(dateValue).format("YYYY-MM-DD") : null;
    const currentGroup = bookingGroups.find((group) => group.id === groupId);

    updateGroup(groupId, {
      date: normalizedDate,
      time: null,
    });

    fetchAvailableSlots(
      groupId,
      normalizedDate,
      currentGroup?.serviceId || null,
      currentGroup?.employeeId || null
    );
  };

  const addBookingGroup = () => {
    setBookingGroups((currentGroups) => [
      ...currentGroups,
      createEmptyBookingGroup(preselectedDate || null),
    ]);
  };

  const removeBookingGroup = (groupId) => {
    setBookingGroups((currentGroups) =>
      currentGroups.length > 1
        ? currentGroups.filter((group) => group.id !== groupId)
        : currentGroups
    );
  };

  const handleSubmit = async () => {
    if (!selectedUserId) {
      const hasMissingUserFields =
        !userForm.name.trim() ||
        !userForm.lastName.trim() ||
        !userForm.phone.trim() ||
        !userForm.email.trim();
      if (hasMissingUserFields) {
        message.warning("Completá nombre, apellido, teléfono y email del usuario");
        return;
      }
    }

    const incompleteGroup = bookingGroups.find(
      (group) => !group.serviceId || !group.employeeId || !group.date || !group.time
    );

    if (incompleteGroup) {
      message.warning("Completá servicio, profesional, fecha y horario en cada bloque");
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem("token");
      const payload = {
        userId: selectedUserId || undefined,
        user: selectedUserId
          ? undefined
          : {
              name: userForm.name.trim(),
              lastName: userForm.lastName.trim(),
              phone: userForm.phone.trim(),
              email: userForm.email.trim(),
              type_client: userForm.type_client,
            },
        bookingGroups: bookingGroups.map((group) => ({
          id: group.id,
          date: group.date,
          time: group.time,
          selections: [
            {
              serviceId: Number(group.serviceId),
              employeeId: Number(group.employeeId),
            },
          ],
        })),
      };

      const response = await axios.post(`${API_BASE}/admin/booking`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      message.success("Reservación creada correctamente");
      const createdPassword = response.data?.user?.admin_visible_password;
      if (createdPassword && !selectedUserId) {
        Modal.success({
          title: "Cliente creado correctamente",
          content: `Contraseña generada: ${createdPassword}`,
        });
      }
      const firstDate = bookingGroups[0]?.date;
      navigate(firstDate ? `/admin/agenda/${firstDate}` : "/admin/agenda");
    } catch (error) {
      message.error(error.response?.data?.message || "No se pudo crear la reservación");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100%",
        background:
          "radial-gradient(circle at top left, rgba(219,234,254,0.8), transparent 22%), linear-gradient(180deg, #f8fbff 0%, #ffffff 42%)",
      }}
    >
      <div style={shellStyle}>
        <Card style={panelSectionStyle} bodyStyle={{ padding: 28, overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 18,
              flexWrap: "wrap",
            }}
          >
            <Space size="middle" align="start">
              <Button
                shape="circle"
                size="large"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(-1)}
                style={{
                  width: 46,
                  minWidth: 46,
                  height: 46,
                  borderColor: "#dbe4ee",
                  color: "#344054",
                  background: "#fff",
                  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
                }}
              />
              <div>
                <Text
                  style={{
                    display: "block",
                    color: "#2563eb",
                    fontSize: 12,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: 1.2,
                    marginBottom: 4,
                  }}
                >
                  Gestión de reservaciones
                </Text>
                <Title level={2} style={{ margin: 0, color: "#0f172a" }}>
                  Nueva reservación
                </Title>
                <Text type="secondary" style={{ fontSize: 15 }}>
                  Cargá el cliente y organizá los servicios por bloque de atención.
                </Text>
              </div>
            </Space>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(140px, 1fr))",
                gap: 12,
                minWidth: 300,
                flex: "1 1 320px",
                maxWidth: 420,
              }}
            >
              <Card bordered={false} style={statCardStyle}>
                <Text type="secondary">Servicios</Text>
                <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
                  {summary.totalServices}
                </div>
              </Card>
              <Card bordered={false} style={statCardStyle}>
                <Text type="secondary">Listos</Text>
                <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
                  {summary.scheduledServices}
                </div>
              </Card>
            </div>
          </div>

          <Alert
            style={{
              marginTop: 20,
              borderRadius: 14,
              border: "1px solid #b6dcff",
            }}
            type="info"
            showIcon
            message="Cómo cargar una reservación"
            description="Completá primero los datos del cliente. Después elegí área del salón, tipo de servicio, servicio, profesional, fecha y horario para cada bloque."
          />
        </Card>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.3fr) minmax(320px, 0.7fr)",
          gap: 18,
          alignItems: "start",
          marginTop: 18,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Card
            style={panelSectionStyle}
            bodyStyle={{ padding: 24 }}
          >
            <div style={{ marginBottom: 16 }}>
              <Text
                style={{
                  display: "block",
                  color: "#2563eb",
                  fontSize: 12,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 1.1,
                  marginBottom: 4,
                }}
              >
                Cliente
              </Text>
              <Title level={4} style={{ margin: 0, color: "#0f172a" }}>
                Usuario de la reservación
              </Title>
            </div>
            <Space direction="vertical" size={14} style={{ width: "100%" }}>
              <div style={fieldGridStyle}>
                <div>
                  <span style={labelStyle}>Nombre</span>
                  <Input
                    placeholder="Nombre"
                    size="large"
                    value={userForm.name}
                    disabled={Boolean(detectedUser)}
                    onChange={(event) =>
                      setUserForm((current) => ({ ...current, name: event.target.value }))
                    }
                    style={{ borderRadius: 12 }}
                  />
                </div>
                <div>
                  <span style={labelStyle}>Apellido</span>
                  <Input
                    placeholder="Apellido"
                    size="large"
                    value={userForm.lastName}
                    disabled={Boolean(detectedUser)}
                    onChange={(event) =>
                      setUserForm((current) => ({ ...current, lastName: event.target.value }))
                    }
                    style={{ borderRadius: 12 }}
                  />
                </div>
                <div>
                  <span style={labelStyle}>Teléfono</span>
                  <Input
                    placeholder="Teléfono"
                    size="large"
                    value={userForm.phone}
                    disabled={Boolean(detectedUser)}
                    onChange={(event) =>
                      setUserForm((current) => ({ ...current, phone: event.target.value }))
                    }
                    style={{ borderRadius: 12 }}
                  />
                </div>
                <div>
                  <span style={labelStyle}>Email</span>
                  <Input
                    placeholder="Email"
                    size="large"
                    value={userForm.email}
                    onChange={(event) => {
                      const nextEmail = event.target.value;
                      setUserForm((current) => ({ ...current, email: nextEmail }));
                      syncDetectedUserByEmail(nextEmail);
                    }}
                    style={{ borderRadius: 12 }}
                  />
                </div>
                <div>
                  <span style={labelStyle}>Tipo de cliente</span>
                  <Select
                    size="large"
                    value={userForm.type_client}
                    disabled={Boolean(detectedUser)}
                    onChange={(value) =>
                      setUserForm((current) => ({ ...current, type_client: value }))
                    }
                    options={[
                      { value: "reliable", label: "Confiable" },
                      { value: "new", label: "Nuevo" },
                    ]}
                    style={{ width: "100%" }}
                  />
                </div>
              </div>

              {detectedUser ? (
                <div
                  style={{
                    padding: 16,
                    borderRadius: 16,
                    background: "linear-gradient(180deg, #f8fbff 0%, #f3f7fc 100%)",
                    border: "1px solid #dce7f3",
                  }}
                >
                  <Text strong style={{ display: "block", color: "#0f172a" }}>
                    Usuario detectado automáticamente
                  </Text>
                  <Text type="secondary">
                    {detectedUser?.name} {detectedUser?.lastName}
                    {detectedUser?.phone ? ` · ${detectedUser.phone}` : ""}
                    {detectedUser?.email ? ` · ${detectedUser.email}` : ""}
                  </Text>
                </div>
              ) : null}
            </Space>
          </Card>

          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            {bookingGroups.map((group, index) => {
          const selectedCategory = categories.find(
            (category) => Number(category.id) === Number(group.categoryId)
          );
          const subCategoryOptions = (selectedCategory?.SubCategories || []).map((subCategory) => ({
            value: subCategory.id,
            label: subCategory.name,
          }));
          const selectedSubCategory = (selectedCategory?.SubCategories || []).find(
            (subCategory) => Number(subCategory.id) === Number(group.subCategoryId)
          );
          const serviceOptions = (selectedSubCategory?.Services || [])
            .filter((service) => Array.isArray(service.employees) && service.employees.length)
            .map((service) => ({
              value: service.id,
              label: service.name,
            }));
          const selectedService = findServiceById(group.serviceId);
          const employeeOptions = (selectedService?.employees || [])
            .filter((employee) => employee.active)
            .map((employee) => ({
              value: employee.id,
              label: employee.name,
            }));

              return (
                <Card
                  key={group.id}
                  title={
                    <div>
                      <Text
                        style={{
                          display: "block",
                          color: "#2563eb",
                          fontSize: 12,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          marginBottom: 2,
                        }}
                      >
                        Bloque de atención
                      </Text>
                      <span style={{ fontWeight: 700, color: "#0f172a" }}>
                        Servicio {index + 1}
                      </span>
                    </div>
                  }
                  extra={
                    bookingGroups.length > 1 ? (
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => removeBookingGroup(group.id)}
                      >
                        Quitar
                      </Button>
                    ) : null
                  }
                  style={panelSectionStyle}
                  bodyStyle={{ padding: 24 }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) auto",
                      gap: 12,
                      alignItems: "start",
                      marginBottom: 18,
                      paddingBottom: 14,
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <div>
                      <Text style={{ ...labelStyle, marginBottom: 2 }}>
                        Estructura del bloque
                      </Text>
                      <Text type="secondary">
                        Seleccioná el recorrido del servicio hasta definir el turno.
                      </Text>
                    </div>
                    <div
                      style={{
                        padding: "8px 12px",
                        borderRadius: 999,
                        background: "#eff6ff",
                        border: "1px solid #dbeafe",
                        color: "#2563eb",
                        fontSize: 12,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Bloque {index + 1}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: 12,
                    }}
                  >
                    <div>
                      <span style={labelStyle}>Área del salón</span>
                      <Select
                        size="large"
                        placeholder="Categoría"
                        value={group.categoryId}
                        onChange={(value) => handleCategoryChange(group.id, value)}
                        options={categories.map((category) => ({
                          value: category.id,
                          label: category.name,
                        }))}
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div>
                      <span style={labelStyle}>Tipo de servicio</span>
                      <Select
                        size="large"
                        placeholder="Tipo de servicio"
                        value={group.subCategoryId}
                        onChange={(value) => handleSubCategoryChange(group.id, value)}
                        disabled={!group.categoryId}
                        options={subCategoryOptions}
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div>
                      <span style={labelStyle}>Servicio</span>
                      <Select
                        size="large"
                        placeholder="Servicio"
                        value={group.serviceId}
                        onChange={(value) => handleServiceChange(group.id, value)}
                        disabled={!group.subCategoryId}
                        options={serviceOptions}
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div>
                      <span style={labelStyle}>Profesional</span>
                      <Select
                        size="large"
                        placeholder="Profesional"
                        value={group.employeeId}
                        onChange={(value) => handleEmployeeChange(group.id, value)}
                        disabled={!group.serviceId}
                        options={employeeOptions}
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div>
                      <span style={labelStyle}>Fecha</span>
                      <DatePicker
                        size="large"
                        format="DD/MM/YYYY"
                        value={group.date ? dayjs(group.date) : null}
                        onChange={(value) => handleDateChange(group.id, value)}
                        disabledDate={(current) =>
                          current && current.startOf("day").isBefore(dayjs().startOf("day"))
                        }
                        style={{ width: "100%", borderRadius: 12 }}
                      />
                    </div>
                    <div>
                      <span style={labelStyle}>Horario</span>
                      <Select
                        size="large"
                        placeholder="Horario"
                        value={group.time}
                        onChange={(value) => updateGroup(group.id, { time: value })}
                        disabled={!group.employeeId || !group.date}
                        loading={group.loadingSlots}
                        options={(group.availableSlots || [])
                          .filter((slot) => slot.available)
                          .map((slot) => ({
                            value: slot.time,
                            label: slot.time,
                          }))}
                        style={{ width: "100%" }}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 16,
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    {group.categoryId && (
                      <span
                        style={{
                          padding: "7px 12px",
                          borderRadius: 999,
                          background: "#eef4ff",
                          color: "#2563eb",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        Área elegida
                      </span>
                    )}
                    {group.subCategoryId && (
                      <span
                        style={{
                          padding: "7px 12px",
                          borderRadius: 999,
                          background: "#f3e8ff",
                          color: "#7c3aed",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        Tipo definido
                      </span>
                    )}
                    {group.serviceId && (
                      <span
                        style={{
                          padding: "7px 12px",
                          borderRadius: 999,
                          background: "#eef4ff",
                          color: "#2563eb",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        Servicio elegido
                      </span>
                    )}
                    {group.employeeId && (
                      <span
                        style={{
                          padding: "7px 12px",
                          borderRadius: 999,
                          background: "#ecfdf3",
                          color: "#059669",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        Profesional asignado
                      </span>
                    )}
                    {group.date && group.time && (
                      <span
                        style={{
                          padding: "7px 12px",
                          borderRadius: 999,
                          background: "#fff7ed",
                          color: "#ea580c",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        Horario confirmado
                      </span>
                    )}
                  </div>
                </Card>
              );
            })}
          </Space>

      <Space style={{ marginTop: 18 }} wrap>
        <Button
          icon={<PlusOutlined />}
          onClick={addBookingGroup}
          size="large"
          style={{
            height: 48,
            borderRadius: 14,
            borderColor: "#d8e2ec",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
          }}
        >
          Agregar servicio
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={saving}
          size="large"
          style={{
            height: 48,
            borderRadius: 14,
            minWidth: 220,
            background: "linear-gradient(135deg, #1677ff 0%, #0f52d6 100%)",
            boxShadow: "0 16px 30px rgba(22, 119, 255, 0.28)",
          }}
        >
          Guardar reservación
        </Button>
      </Space>

        </div>

        <Card
          style={{ ...panelSectionStyle, position: "sticky", top: 20 }}
          bodyStyle={{ padding: 24 }}
        >
          <Text
            style={{
              display: "block",
              color: "#2563eb",
              fontSize: 12,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: 1.1,
              marginBottom: 4,
            }}
          >
            Resumen
          </Text>
          <Title level={4} style={{ marginTop: 0, color: "#0f172a" }}>
            Estado de la reservación
          </Title>

          <div style={{ display: "grid", gap: 12, marginTop: 18 }}>
            <div
              style={{
                padding: 16,
                borderRadius: 18,
                background: "#f8fbff",
                border: "1px solid #dbe7f6",
              }}
            >
              <Text type="secondary">Servicios cargados</Text>
              <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
                {summary.totalServices}
              </div>
            </div>

            <div
              style={{
                padding: 16,
                borderRadius: 18,
                background: "#f8fbff",
                border: "1px solid #dbe7f6",
              }}
            >
              <Text type="secondary">Bloques completos</Text>
              <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
                {summary.scheduledServices}
              </div>
            </div>

            <div
              style={{
                padding: 16,
                borderRadius: 18,
                background: "linear-gradient(180deg, #fdfefe 0%, #f5f9ff 100%)",
                border: "1px solid #dbe7f6",
              }}
            >
              <Text type="secondary">Reserva estimada</Text>
              <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
                ${summary.totalReservation}
              </div>
              <Text type="secondary">Duración total: {summary.totalDuration} min</Text>
            </div>
          </div>

          <div
            style={{
              marginTop: 18,
              padding: 16,
              borderRadius: 18,
              background: "#fffaf0",
              border: "1px solid #fde7bf",
            }}
          >
            <Text strong style={{ display: "block", color: "#7c4a03", marginBottom: 6 }}>
              Recomendación
            </Text>
            <Text type="secondary">
              Verificá que cada bloque tenga servicio, profesional, fecha y horario antes de guardar.
            </Text>
          </div>
        </Card>
      </div>
      </div>
    </div>
  );
};

export default CreateReservation;
